/**
 * Year Toggle Controls - Allow users to show/hide individual years in charts
 * Supports 2-series (TTM) and 3-series (3-year view) modes
 */

import React from 'react';
import { Eye, EyeOff } from 'lucide-react';

export interface YearVisibilityState {
  show2023: boolean;
  show2024: boolean;
  show2025: boolean;
}

interface YearToggleControlsProps {
  visibility: YearVisibilityState;
  onVisibilityChange: (visibility: YearVisibilityState) => void;
  mode: '2-series' | '3-series'; // TTM vs 3-year view
  seriesNames?: string[]; // Actual series names from API (for TTM: ["Previous TTM", "Current TTM"])
}

const YearToggleControls: React.FC<YearToggleControlsProps> = ({
  visibility,
  onVisibilityChange,
  mode,
  seriesNames = []
}) => {
  // For TTM mode, we only show 2024/2025 toggles (mapped to Previous/Current TTM)
  // For 3-year view, we show all three year toggles
  const yearOptions = mode === '3-series' ? [
    {
      key: 'show2023' as keyof YearVisibilityState,
      label: '2023',
      color: '#808080',
      actualName: seriesNames[0] || '2023'
    },
    {
      key: 'show2024' as keyof YearVisibilityState,
      label: '2024',
      color: '#000000',
      actualName: seriesNames[1] || '2024'
    },
    {
      key: 'show2025' as keyof YearVisibilityState,
      label: '2025',
      color: '#633FF3',
      actualName: seriesNames[2] || '2025'
    }
  ] : [
    {
      key: 'show2024' as keyof YearVisibilityState,
      label: seriesNames[0] || 'Previous TTM',
      color: '#000000',
      actualName: seriesNames[0] || 'Previous TTM'
    },
    {
      key: 'show2025' as keyof YearVisibilityState,
      label: seriesNames[1] || 'Current TTM',
      color: '#633FF3',
      actualName: seriesNames[1] || 'Current TTM'
    }
  ];

  const toggleYear = (key: keyof YearVisibilityState) => {
    onVisibilityChange({
      ...visibility,
      [key]: !visibility[key]
    });
  };

  const visibleCount = yearOptions.filter(option => visibility[option.key]).length;

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-gray-600 font-medium">Show:</span>
      <div className="flex gap-1">
        {yearOptions.map((option) => {
          const isVisible = visibility[option.key];
          return (
            <button
              key={option.key}
              onClick={() => toggleYear(option.key)}
              disabled={visibleCount === 1 && isVisible} // Prevent hiding the last visible year
              className={`
                px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-medium
                transition-all duration-200 border
                ${isVisible
                  ? 'bg-white text-gray-700 border-gray-300 shadow-sm'
                  : 'bg-gray-100 text-gray-400 border-gray-200'
                }
                ${visibleCount === 1 && isVisible
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:shadow-md cursor-pointer'
                }
              `}
              title={isVisible ? `Hide ${option.actualName}` : `Show ${option.actualName}`}
            >
              {/* Color indicator */}
              <div
                className={`w-3 h-3 rounded-sm ${isVisible ? 'opacity-100' : 'opacity-30'}`}
                style={{ backgroundColor: option.color }}
              />

              {/* Year label */}
              <span className={isVisible ? 'text-gray-700' : 'text-gray-400'}>
                {option.label}
              </span>

              {/* Visibility icon */}
              {isVisible ? (
                <Eye className="w-3 h-3 text-gray-500" />
              ) : (
                <EyeOff className="w-3 h-3 text-gray-400" />
              )}
            </button>
          );
        })}
      </div>

      {/* Info text */}
      <span className="text-xs text-gray-500 ml-2">
        {visibleCount} of {yearOptions.length} visible
      </span>
    </div>
  );
};

export default YearToggleControls;